import * as React from "react";
import {Alert, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle} from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import {gql} from "@apollo/client";
import {toast} from "react-toastify";
import LoadingDialog from "../../components/loadingDialog";
import {mutate} from "../../lib/apollo";
import {
    conversionStatPresetDeleteDetails,
    ConversionStatPresetRow,
} from "./conversionStatPreset";

export default function ConversionStatPresetDeleteDialog(params: {
    preset: ConversionStatPresetRow | null;
    setPreset: (preset: ConversionStatPresetRow | null) => void;
    currentUserId?: number | null;
    onDeleted: () => void | Promise<void>;
}) {
    const [loading, setLoading] = React.useState(false);

    const handleClose = (_event?: any, reason?: any) => {
        if (reason === "backdropClick" || loading) return;
        params.setPreset(null);
    };

    const handleDelete = async () => {
        if (!params.preset?.id) return;
        setLoading(true);
        const response = await mutate(
            gql`mutation m($id: Int!) { removeConversionStatPreset(id: $id) }`,
            {id: params.preset.id},
        );
        if (response && response.removeConversionStatPreset) {
            toast.success(
                <>
                    Deleted preset <Box component="span" sx={{fontWeight: "bold"}}>{params.preset.name}</Box>
                </>,
            );
            params.setPreset(null);
            await params.onDeleted();
        }
        setLoading(false);
    };

    if (!params.preset) return <></>;

    const details = conversionStatPresetDeleteDetails(params.preset, params.currentUserId);

    return (
        <Dialog
            open={!!params.preset}
            onClose={handleClose}
            aria-labelledby="preset-delete-title"
            aria-describedby="preset-delete-description"
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle id="preset-delete-title">Delete Preset?</DialogTitle>
            <DialogContent>
                <DialogContentText id="preset-delete-description" component="div">
                    Are you sure you want to delete{" "}
                    <Box component="span" sx={{fontWeight: "bold"}}>{details.name}</Box>?
                    This cannot be undone.
                    <Box sx={{mt: 2, display: "grid", gridTemplateColumns: "auto 1fr", columnGap: 2, rowGap: 0.75}}>
                        {details.lines.map((line) => (
                            <React.Fragment key={line.label}>
                                <Box sx={{color: "text.secondary", fontWeight: 500}}>{line.label}</Box>
                                <Box sx={{wordBreak: "break-word"}}>{line.value}</Box>
                            </React.Fragment>
                        ))}
                    </Box>
                </DialogContentText>
                {details.shareWarning && (
                    <Alert severity="warning" sx={{mt: 2}}>{details.shareWarning}</Alert>
                )}
            </DialogContent>
            {loading ? <LoadingDialog/> : (
                <DialogActions>
                    <Button sx={{color: "red"}} onClick={handleClose}>No</Button>
                    <Button sx={{color: "green"}} onClick={handleDelete} autoFocus>Yes, delete</Button>
                </DialogActions>
            )}
        </Dialog>
    );
}
